/**
 * TransfersTab — Lista de transferencias entre bodegas con modal de creación.
 */
import { useState } from 'react'
import { Plus, ArrowRight, Truck, FileText } from 'lucide-react'
import { DataTable } from '@/components/ds/DataTable'
import { EmptyState } from '@/components/EmptyState'
import { useTransfers, useCreateTransfer, type StockTransfer } from '../hooks/useInventory'
import { usePermission } from '../../../hooks/usePermission'
import { useTenantLocale, formatDate } from '@/lib/locale'
import { ToastContainer, useToast } from '../../../components/Toast'
import { extractErrorMessage } from '../../../lib/errorUtils'
import TransferModal from './TransferModal'
import { Pagination } from '@/components/ds/Pagination'

const STATUS_BADGE: Record<string, { label: string; cls: string }> = {
  draft:      { label: 'Borrador', cls: 'bg-surface-muted text-content-muted' },
  in_transit: { label: 'En Tránsito', cls: 'bg-warning-surface-strong text-warning-content' },
  received:   { label: 'Recibido', cls: 'bg-success-surface-strong text-success-content' },
  cancelled:  { label: 'Anulado', cls: 'bg-danger-surface-strong text-danger-content' },
}

export default function TransfersTab() {
  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState('')
  const [modalOpen, setModalOpen] = useState(false)
  const { can } = usePermission()
  const locale = useTenantLocale()
  const toast = useToast()

  const { data, isLoading } = useTransfers({
    status: statusFilter || undefined,
    page,
    page_size: 20,
  })
  const { mutate: createTransfer, isPending } = useCreateTransfer()

  const transfers = data?.transfers || []
  const total = data?.count || 0

  const handleCreate = (formData: any) => {
    createTransfer(formData, {
      onSuccess: () => {
        toast.success('Transferencia creada. Despáchela cuando esté lista.')
        setModalOpen(false)
      },
      onError: (e: any) => toast.error(extractErrorMessage(e, 'Error al crear transferencia')),
    })
  }

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <span className="text-sm text-content-muted">{total} transferencias</span>
          <select aria-label="Todos los estados"
            value={statusFilter}
            onChange={e => { setStatusFilter(e.target.value); setPage(1) }}
            className="px-3 py-1.5 text-sm border border-input-border rounded-lg bg-input-bg text-content"
          >
            <option value="">Todos los estados</option>
            <option value="draft">Borrador</option>
            <option value="in_transit">En Tránsito</option>
            <option value="received">Recibido</option>
            <option value="cancelled">Anulado</option>
          </select>
        </div>
        {can('inventory.transfers', 'create') && (
          <button
            onClick={() => setModalOpen(true)}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-primary text-on-primary rounded-lg hover:bg-primary-hover"
          >
            <Plus size={14} /> Nueva transferencia
          </button>
        )}
      </div>

      <DataTable
        columns={[
          {
            key: 'ruta', header: 'Origen → Destino',
            render: (t: StockTransfer) => (
              <>
                <span className="font-medium text-content">{t.from_warehouse_code}</span>
                <ArrowRight size={14} className="inline mx-2 text-content-subtle" />
                <span className="font-medium text-content">{t.to_warehouse_code}</span>
                <span className="ml-2 text-xs text-content-subtle">{t.to_warehouse_name}</span>
              </>
            ),
          },
          {
            key: 'status', header: 'Estado',
            render: (t: StockTransfer) => {
              const badge = STATUS_BADGE[t.status] || STATUS_BADGE.draft
              return (
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full ${badge.cls}`}>
                  {t.status === 'in_transit' && <Truck size={12} />}
                  {badge.label}
                </span>
              )
            },
          },
          { key: 'lines_count', header: 'Productos', numeric: true, render: (t: StockTransfer) => t.lines_count || 0 },
          {
            key: 'dte', header: 'Guía Despacho',
            render: (t: StockTransfer) => (t as any).dte_document
              ? <span className="inline-flex items-center gap-1 text-xs text-primary font-medium"><FileText size={12} /> GD vinculada</span>
              : <span className="text-xs text-content-subtle">—</span>,
          },
          { key: 'transfer_date', header: 'Despacho', render: (t: StockTransfer) => t.transfer_date || '—' },
          { key: 'received_date', header: 'Recepción', render: (t: StockTransfer) => t.received_date || '—' },
          { key: 'created_at', header: 'Creado', render: (t: StockTransfer) => <span className="text-xs">{formatDate(t.created_at, locale)}</span> },
        ]}
        rows={transfers}
        rowKey={(t: StockTransfer) => t.id}
        loading={isLoading}
        empty={<EmptyState icon={Truck} size="sm" title="Sin transferencias registradas." />}
        minWidth={900}
        caption="Transferencias entre bodegas"
      />

      {total > 20 && (
        <Pagination
          page={page}
          pageSize={20}
          total={total}
          onPageChange={setPage}
          locale={locale}
        />
      )}

      <TransferModal open={modalOpen} onClose={() => setModalOpen(false)} onSubmit={handleCreate} isPending={isPending} />
      <ToastContainer toasts={toast.toasts} onDismiss={toast.dismiss} />
    </>
  )
}
